import { Logger } from '@egw/logger'
import type {
  RSAnalytics,
  RSALogger,
  RSAEvent,
  RSACustomIntegration,
  CustomDestinationConfig,
} from '@rudderstack/analytics-js'

const log = new Logger('Analytics Service - Braze Custom Integration')

// Declare window.evergreenBraze interface
declare global {
  interface Window {
    evergreenBraze?: {
      initializeBrazeSDK: (_config: { apiKey: string; endpoint: string; enableLogging?: boolean }) => void
      identifyUser: (
        _userId: string,
        _traits?: {
          email?: string
          firstName?: string
          lastName?: string
          phone?: string
        },
      ) => void
      trackEvent: (_eventName: string, _properties?: Record<string, unknown>) => void
      isReady: () => boolean
    }
  }
}

/**
 * Braze Custom Integration for RudderStack v3
 *
 * This integration acts as a bridge between RudderStack and the Braze Service.
 * Instead of RudderStack initializing Braze directly, this custom integration
 * delegates initialization and all SDK interactions to window.evergreenBraze,
 * which is exposed by the Consumer Engagement Service.
 *
 * Architecture:
 * RudderStack → Braze Custom Integration → window.evergreenBraze → Braze SDK
 *
 * Benefits:
 * - Decouples Analytics Service from Consumer Engagement Service
 * - Braze Service maintains full control over SDK initialization
 * - Clear separation of concerns between analytics and consumer engagement
 */
export const createBrazeCustomIntegration = (config: {
  apiKey: string
  apiEndpoint: string
  enableLogging?: boolean
}): RSACustomIntegration => {
  log.info('Creating Braze custom integration', { apiEndpoint: config.apiEndpoint })

  return {
    /**
     * Initialize Braze SDK via window.evergreenBraze
     * Called by RudderStack during destination configuration
     *
     * @param destinationConfig - The custom destination configuration from RudderStack dashboard
     * @param analytics - RudderStack analytics instance
     * @param logger - RudderStack logger instance
     */
    init: (destinationConfig: CustomDestinationConfig, analytics: RSAnalytics, logger: RSALogger): void => {
      log.debug('Braze custom integration init() called', {
        destinationConfig,
        hasAnalytics: Boolean(analytics),
        hasLogger: Boolean(logger),
      })

      // Wait for window.evergreenBraze to be available
      if (typeof window === 'undefined') {
        log.warn('Window object not available - skipping Braze initialization')
        return
      }

      if (!window.evergreenBraze) {
        log.warn('window.evergreenBraze not available - Braze Service may not be loaded yet')
        return
      }

      try {
        log.debug('Initializing Braze SDK via window.evergreenBraze.initializeBrazeSDK()', {
          apiKey: config.apiKey,
          apiEndpoint: config.apiEndpoint,
          enableLogging: config.enableLogging,
        })

        window.evergreenBraze.initializeBrazeSDK({
          apiKey: config.apiKey,
          endpoint: config.apiEndpoint,
          enableLogging: config.enableLogging ?? false,
        })

        log.info('Braze SDK initialized successfully via custom integration')
      } catch (error) {
        log.error('Failed to initialize Braze SDK via custom integration', error)
      }
    },

    /**
     * Check if the Braze integration is ready to process events
     * REQUIRED method for RSACustomIntegration
     *
     * @param analytics - RudderStack analytics instance
     * @param logger - RudderStack logger instance
     * @returns boolean indicating if Braze is ready
     */
    isReady: (analytics: RSAnalytics, logger: RSALogger): boolean => {
      const ready =
        typeof window !== 'undefined' && Boolean(window.evergreenBraze) && Boolean(window.evergreenBraze?.isReady())

      log.debug('Braze custom integration isReady() check', {
        ready,
        hasWindow: typeof window !== 'undefined',
        hasEvergreenBraze: typeof window !== 'undefined' && Boolean(window.evergreenBraze),
        hasAnalytics: Boolean(analytics),
        hasLogger: Boolean(logger),
      })

      return ready
    },

    /**
     * Process identify events
     * Maps RudderStack identify() calls to window.evergreenBraze.identifyUser()
     *
     * @param analytics - RudderStack analytics instance
     * @param logger - RudderStack logger instance
     * @param event - The identify event payload
     */
    identify: (analytics: RSAnalytics, logger: RSALogger, event: RSAEvent): void => {
      log.debug('Braze custom integration identify() called', {
        analytics,
        logger,
        event,
        userId: event.message.userId,
        hasTraits: Boolean(event.message.context.traits),
      })

      if (!window.evergreenBraze) {
        log.warn('window.evergreenBraze not available - cannot identify user')
        return
      }

      try {
        const userId = event.message.userId
        const traits = event.message.context.traits

        if (userId) {
          log.debug('Identifying user via window.evergreenBraze.identifyUser()', {
            userId,
            traits,
          })

          window.evergreenBraze.identifyUser(userId, {
            email: traits?.email as string | undefined,
            firstName: traits?.firstName as string | undefined,
            lastName: traits?.lastName as string | undefined,
            phone: traits?.phone as string | undefined,
          })

          log.info('User identified successfully in Braze', { userId })
        } else {
          log.warn('No userId provided in identify event')
        }
      } catch (error) {
        log.error('Failed to identify user in Braze', error)
      }
    },

    /**
     * Process track events
     * Maps RudderStack track() calls to window.evergreenBraze.trackEvent()
     *
     * @param analytics - RudderStack analytics instance
     * @param logger - RudderStack logger instance
     * @param event - The track event payload
     */
    track: (_analytics: RSAnalytics, _logger: RSALogger, event: RSAEvent): void => {
      log.debug('Braze custom integration track() called', {
        message: event.message.event,
        properties: event.message.properties,
        hasProperties: Boolean(event.message.properties),
      })

      if (!window.evergreenBraze) {
        log.warn('window.evergreenBraze not available - cannot track event')
        return
      }

      try {
        const eventName = event.message.event
        const properties = event.message.properties as Record<string, unknown> | undefined

        if (eventName) {
          log.debug('Tracking event via window.evergreenBraze.trackEvent()', {
            eventName,
            properties,
          })

          window.evergreenBraze.trackEvent(eventName, properties)

          log.info('Event tracked successfully in Braze', { eventName })
        } else {
          log.warn('No event name provided in track event')
        }
      } catch (error) {
        log.error('Failed to track event in Braze', error)
      }
    },

    /**
     * Process page events
     * Maps RudderStack page() calls to window.evergreenBraze.trackEvent()
     *
     * @param analytics - RudderStack analytics instance
     * @param logger - RudderStack logger instance
     * @param event - The page event payload
     */
    page: (_analytics: RSAnalytics, _logger: RSALogger, event: RSAEvent): void => {
      log.debug('Braze custom integration page() called', {
        name: event.message.name,
        hasProperties: Boolean(event.message.properties),
      })

      if (!window.evergreenBraze) {
        log.warn('window.evergreenBraze not available - cannot track page view')
        return
      }

      try {
        const eventName = (event as { message: { name: string; properties: Record<string, unknown> } }).message.name
        const properties = event.message.properties as Record<string, unknown> | undefined

        log.debug('Tracking page view via window.evergreenBraze.trackEvent()', {
          eventName,
          properties,
        })

        window.evergreenBraze.trackEvent(eventName, properties)

        log.info('Page view tracked successfully in Braze', { eventName })
      } catch (error) {
        log.error('Failed to track page view in Braze', error)
      }
    },
  }
}
